import { apiClient } from '../utils/api-client';
import {
  mapLedgerTransaction,
  mapConvertibleInstrument,
  mapShareClass,
  mapCapTableSnapshot,
  mapStockSplitPreviewRow,
  mapBankEntry,
  mapCaptableFundTransaction,
} from './capTableMapper';
import type {
  CapTableSnapshot,
  CapTableMatrix,
  CapTableViewMode,
  CapTableFinality,
  Stakeholder,
  ShareClass,
  LedgerTransaction,
  ConvertibleDraftCreated,
  ConvertibleRepaymentDraftCreated,
  ConvertibleInstrument,
  CreateStakeholderRequest,
  UpdateStakeholderRequest,
  CreateShareClassRequest,
  CreateTransactionRequest,
  CreateConvertibleRequest,
  CapTableScenario,
  CreateScenarioRequest,
  WaterfallAnalysis,
  WaterfallAnalysisRequest,
  WaterfallCalculationRequest,
  WaterfallResult,
  CapTableValidationResult,
  CapTableExportOptions,
  MaturityResolutionPreview,
  ConvertToSharesAtMaturityRequest,
  ExtendMaturityRequest,
  TransactionDocument,
  AttachDocumentToTransactionRequest,
  ManualApproveRequest,
  ReconciliationState,
  AddBankEntryRequest,
  UpdateBankEntryRequest,
  BankEntry,
  CaptableFundTransaction,
  CaptableGLTransaction,
  WarrantIssuanceRequest,
  WarrantExerciseRequest,
  WarrantDraftCreated,
  WarrantExerciseCreated,
  DeriveConversionRequest,
  DeriveConversionResponse,
  CreateStockSplitRequest,
  UpdateStockSplitRequest,
  PreviewStockSplitRequest,
  StockSplitPreviewRow,
  ApiStockSplitPreviewRow,
} from '../types/captable';
import type { TransactionActivityEntry } from '@/types/transaction-activity';

interface CapTableSnapshotParams {
  view_mode?: CapTableViewMode;
  finality?: CapTableFinality;
  as_of_date?: string;
  conversion_price?: number;
  conversion_valuation?: number;
  valuation_type?: string;
  scenario_id?: string;
}

interface CapTableMatrixParams {
  from_date?: string;
  to_date?: string;
  view_mode?: CapTableViewMode;
  finality?: CapTableFinality;
}

export interface PaginationParams {
  page?: number;
  page_size?: number;
}

interface PaginationInfo {
  page: number;
  page_size: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationInfo;
}

export const capTableService = {
  // Cap Table Snapshots
  async getSnapshot(
    companyId: string,
    params: CapTableSnapshotParams = {}
  ): Promise<CapTableSnapshot> {
    const raw = await apiClient.get<CapTableSnapshot>(
      `/captable/companies/${companyId}/snapshot`,
      { params }
    );
    return mapCapTableSnapshot(raw);
  },

  // Cap Table Evolution Matrix
  async getMatrix(
    companyId: string,
    params: CapTableMatrixParams = {}
  ): Promise<CapTableMatrix> {
    return apiClient.get<CapTableMatrix>(
      `/captable/companies/${companyId}/matrix`,
      { params }
    );
  },

  // Stakeholder Management
  async getStakeholders(companyId: string): Promise<Stakeholder[]> {
    return apiClient.get<Stakeholder[]>(
      `/captable/companies/${companyId}/stakeholders`
    );
  },

  async createStakeholder(
    companyId: string,
    data: CreateStakeholderRequest
  ): Promise<Stakeholder> {
    return apiClient.post<Stakeholder>(
      `/captable/companies/${companyId}/stakeholders`,
      data
    );
  },

  async updateStakeholder(
    companyId: string,
    stakeholderId: string,
    data: UpdateStakeholderRequest
  ): Promise<{ id: string }> {
    return apiClient.put<{ id: string }>(
      `/captable/companies/${companyId}/stakeholders/${stakeholderId}`,
      data
    );
  },

  async deleteStakeholder(
    companyId: string,
    stakeholderId: string
  ): Promise<void> {
    return apiClient.delete(
      `/captable/companies/${companyId}/stakeholders/${stakeholderId}`
    );
  },

  // Share Class Management
  async getShareClasses(companyId: string): Promise<ShareClass[]> {
    const raw = await apiClient.get<ShareClass[]>(
      `/captable/companies/${companyId}/share-classes`
    );
    return raw.map(mapShareClass);
  },

  async createShareClass(
    companyId: string,
    data: CreateShareClassRequest
  ): Promise<ShareClass> {
    const raw = await apiClient.post<ShareClass>(
      `/captable/companies/${companyId}/share-classes`,
      data
    );
    return mapShareClass(raw);
  },

  async updateShareClass(
    companyId: string,
    shareClassId: string,
    data: Partial<CreateShareClassRequest>
  ): Promise<ShareClass> {
    const raw = await apiClient.put<ShareClass>(
      `/captable/companies/${companyId}/share-classes/${shareClassId}`,
      data
    );
    return mapShareClass(raw);
  },

  async deleteShareClass(
    companyId: string,
    shareClassId: string
  ): Promise<void> {
    return apiClient.delete(
      `/captable/companies/${companyId}/share-classes/${shareClassId}`
    );
  },

  // Transaction Management
  async getTransactions(
    companyId: string,
    limit: number = 50
  ): Promise<LedgerTransaction[]> {
    const transactions = await apiClient.get<LedgerTransaction[]>(
      `/captable/companies/${companyId}/transactions?limit=${limit}`
    );
    return transactions.map(mapLedgerTransaction);
  },

  async getTransactionsPaginated(
    companyId: string,
    params: PaginationParams = {}
  ): Promise<PaginatedResponse<LedgerTransaction>> {
    const response = await apiClient.get<PaginatedResponse<LedgerTransaction>>(
      `/captable/companies/${companyId}/transactions/paginated`,
      { params }
    );
    return {
      ...response,
      data: response.data.map(mapLedgerTransaction),
    };
  },

  async createTransaction(
    companyId: string,
    data: CreateTransactionRequest
  ): Promise<LedgerTransaction> {
    const created = await apiClient.post<LedgerTransaction>(
      `/captable/companies/${companyId}/transactions`,
      data
    );
    return mapLedgerTransaction(created);
  },

  async getTransaction(
    companyId: string,
    transactionId: string
  ): Promise<LedgerTransaction> {
    const txn = await apiClient.get<LedgerTransaction>(
      `/captable/companies/${companyId}/transactions/${transactionId}`
    );
    return mapLedgerTransaction(txn);
  },

  async getTransactionActivity(
    companyId: string,
    transactionId: string
  ): Promise<TransactionActivityEntry[]> {
    return apiClient.get<TransactionActivityEntry[]>(
      `/captable/companies/${companyId}/transactions/${transactionId}/activity`
    );
  },

  async getTransactionJournalEntries(
    companyId: string,
    transactionId: string
  ): Promise<CaptableGLTransaction[]> {
    return apiClient.get<CaptableGLTransaction[]>(
      `/captable/companies/${companyId}/transactions/${transactionId}/journal-entries`
    );
  },

  async updateTransaction(
    companyId: string,
    transactionId: string,
    data: Partial<CreateTransactionRequest>
  ): Promise<LedgerTransaction> {
    const updated = await apiClient.put<LedgerTransaction>(
      `/captable/companies/${companyId}/transactions/${transactionId}`,
      data
    );
    return mapLedgerTransaction(updated);
  },

  async deleteTransaction(
    companyId: string,
    transactionId: string
  ): Promise<void> {
    return apiClient.delete(
      `/captable/companies/${companyId}/transactions/${transactionId}`
    );
  },

  // Declarative stock splits
  async createStockSplit(
    companyId: string,
    data: CreateStockSplitRequest
  ): Promise<LedgerTransaction> {
    const created = await apiClient.post<LedgerTransaction>(
      `/captable/companies/${companyId}/transactions/split`,
      data
    );
    return mapLedgerTransaction(created);
  },

  async updateStockSplit(
    companyId: string,
    transactionId: string,
    data: UpdateStockSplitRequest
  ): Promise<LedgerTransaction> {
    const updated = await apiClient.put<LedgerTransaction>(
      `/captable/companies/${companyId}/transactions/${transactionId}/split`,
      data
    );
    return mapLedgerTransaction(updated);
  },

  async previewStockSplit(
    companyId: string,
    data: PreviewStockSplitRequest
  ): Promise<StockSplitPreviewRow[]> {
    const raw = await apiClient.post<ApiStockSplitPreviewRow[]>(
      `/captable/companies/${companyId}/transactions/split/preview`,
      data
    );
    return raw.map(mapStockSplitPreviewRow);
  },

  // Convertible Instruments
  async getConvertibles(
    companyId: string,
    params?: { status?: string; as_of_date?: string }
  ): Promise<ConvertibleInstrument[]> {
    const raw = await apiClient.get<ConvertibleInstrument[]>(
      `/captable/companies/${companyId}/convertibles`,
      { params }
    );
    return raw.map(mapConvertibleInstrument);
  },

  async getConvertibleDetail(
    companyId: string,
    convertibleId: string
  ): Promise<ConvertibleInstrument> {
    const raw = await apiClient.get<ConvertibleInstrument>(
      `/captable/companies/${companyId}/convertibles/${convertibleId}`
    );
    return mapConvertibleInstrument(raw);
  },

  async createConvertible(
    companyId: string,
    data: CreateConvertibleRequest
  ): Promise<ConvertibleDraftCreated> {
    return apiClient.post<ConvertibleDraftCreated>(
      `/captable/companies/${companyId}/convertibles`,
      data
    );
  },

  async issueWarrant(
    companyId: string,
    data: WarrantIssuanceRequest
  ): Promise<WarrantDraftCreated> {
    return apiClient.post<WarrantDraftCreated>(
      `/captable/companies/${companyId}/warrants`,
      data
    );
  },

  async exerciseWarrant(
    companyId: string,
    instrumentId: string,
    data: WarrantExerciseRequest
  ): Promise<WarrantExerciseCreated> {
    return apiClient.post<WarrantExerciseCreated>(
      `/captable/companies/${companyId}/warrants/${instrumentId}/exercise`,
      data
    );
  },

  async updateConvertible(
    companyId: string,
    convertibleId: string,
    data: Partial<CreateConvertibleRequest>
  ): Promise<ConvertibleInstrument> {
    const raw = await apiClient.put<ConvertibleInstrument>(
      `/captable/companies/${companyId}/convertibles/${convertibleId}`,
      data
    );
    return mapConvertibleInstrument(raw);
  },

  async convertInstrument(
    companyId: string,
    convertibleId: string,
    transactionId: string
  ): Promise<ConvertibleInstrument> {
    const raw = await apiClient.post<ConvertibleInstrument>(
      `/captable/companies/${companyId}/convertibles/${convertibleId}/convert`,
      { transaction_id: transactionId }
    );
    return mapConvertibleInstrument(raw);
  },

  async deleteConvertible(
    companyId: string,
    convertibleId: string
  ): Promise<void> {
    return apiClient.delete(
      `/captable/companies/${companyId}/convertibles/${convertibleId}`
    );
  },

  async getMaturityResolutionPreview(
    companyId: string,
    convertibleId: string,
    pricePerShare?: number
  ): Promise<MaturityResolutionPreview> {
    const params = pricePerShare ? { price_per_share: pricePerShare } : {};
    return apiClient.get<MaturityResolutionPreview>(
      `/captable/companies/${companyId}/convertibles/${convertibleId}/maturity-preview`,
      { params }
    );
  },

  async convertToSharesAtMaturity(
    companyId: string,
    convertibleId: string,
    data: ConvertToSharesAtMaturityRequest
  ): Promise<ConvertibleInstrument> {
    const raw = await apiClient.post<ConvertibleInstrument>(
      `/captable/companies/${companyId}/convertibles/${convertibleId}/convert-to-shares`,
      data
    );
    return mapConvertibleInstrument(raw);
  },

  async extendMaturity(
    companyId: string,
    convertibleId: string,
    data: ExtendMaturityRequest
  ): Promise<ConvertibleInstrument> {
    const raw = await apiClient.post<ConvertibleInstrument>(
      `/captable/companies/${companyId}/convertibles/${convertibleId}/extend-maturity`,
      data
    );
    return mapConvertibleInstrument(raw);
  },

  async repayInCash(
    companyId: string,
    convertibleId: string
  ): Promise<ConvertibleRepaymentDraftCreated> {
    return apiClient.post<ConvertibleRepaymentDraftCreated>(
      `/captable/companies/${companyId}/convertibles/${convertibleId}/repay-cash`
    );
  },

  // Scenario Modeling
  async getScenarios(companyId: string): Promise<CapTableScenario[]> {
    return apiClient.get<CapTableScenario[]>(
      `/portfolio/${companyId}/scenarios`
    );
  },

  async createScenario(
    companyId: string,
    data: CreateScenarioRequest
  ): Promise<CapTableScenario> {
    return apiClient.post<CapTableScenario>(
      `/portfolio/${companyId}/scenarios`,
      data
    );
  },

  async getScenario(
    companyId: string,
    scenarioId: string
  ): Promise<CapTableScenario> {
    return apiClient.get<CapTableScenario>(
      `/portfolio/${companyId}/scenarios/${scenarioId}`
    );
  },

  async updateScenario(
    companyId: string,
    scenarioId: string,
    data: Partial<CreateScenarioRequest>
  ): Promise<CapTableScenario> {
    return apiClient.put<CapTableScenario>(
      `/portfolio/${companyId}/scenarios/${scenarioId}`,
      data
    );
  },

  async deleteScenario(companyId: string, scenarioId: string): Promise<void> {
    return apiClient.delete(`/portfolio/${companyId}/scenarios/${scenarioId}`);
  },

  // Waterfall Analysis
  async createWaterfallAnalysis(
    companyId: string,
    data: WaterfallAnalysisRequest
  ): Promise<WaterfallAnalysis> {
    return apiClient.post<WaterfallAnalysis>(
      `/portfolio/${companyId}/waterfall`,
      data
    );
  },

  async getWaterfallAnalyses(companyId: string): Promise<WaterfallAnalysis[]> {
    return apiClient.get<WaterfallAnalysis[]>(
      `/portfolio/${companyId}/waterfall`
    );
  },

  async getWaterfallAnalysis(
    companyId: string,
    analysisId: string
  ): Promise<WaterfallAnalysis> {
    return apiClient.get<WaterfallAnalysis>(
      `/portfolio/${companyId}/waterfall/${analysisId}`
    );
  },

  async runWaterfallAnalysis(
    companyId: string,
    request: WaterfallCalculationRequest
  ): Promise<WaterfallResult> {
    const response = await apiClient.post<{
      result: WaterfallResult;
      share_classes_with_prefs: unknown[];
    }>(`/portfolio/${companyId}/waterfall`, request);
    return response.result;
  },

  async getWaterfallScenarios(companyId: string): Promise<unknown[]> {
    return apiClient.get<unknown[]>(`/portfolio/${companyId}/scenarios`);
  },

  // Analytics and Reporting
  // Note: getSummaryMetrics removed - endpoint not implemented in backend

  async validateCapTable(companyId: string): Promise<CapTableValidationResult> {
    return apiClient.get<CapTableValidationResult>(
      `/captable/companies/${companyId}/validate`
    );
  },

  // Import/Export
  async exportCapTable(
    companyId: string,
    options: CapTableExportOptions
  ): Promise<Blob> {
    const queryParams = new URLSearchParams();

    queryParams.append('format', options.format);
    queryParams.append('view_mode', options.view_mode);
    queryParams.append(
      'include_convertibles',
      options.include_convertibles.toString()
    );
    queryParams.append('include_options', options.include_options.toString());
    queryParams.append(
      'include_historical',
      options.include_historical.toString()
    );

    if (options.date_range) {
      queryParams.append('from_date', options.date_range.from_date);
      queryParams.append('to_date', options.date_range.to_date);
    }

    if (options.stakeholder_filter?.length) {
      options.stakeholder_filter.forEach((id) =>
        queryParams.append('stakeholder_filter', id)
      );
    }

    if (options.share_class_filter?.length) {
      options.share_class_filter.forEach((id) =>
        queryParams.append('share_class_filter', id)
      );
    }

    const url = `/captable/companies/${companyId}/export?${queryParams.toString()}`;
    return apiClient.get<Blob>(url);
  },

  async importCapTable(
    companyId: string,
    file: File,
    options: { mapping: Record<string, string>; dry_run?: boolean }
  ): Promise<CapTableValidationResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(options.mapping));
    if (options.dry_run !== undefined) {
      formData.append('dry_run', options.dry_run.toString());
    }

    return apiClient.post<CapTableValidationResult>(
      `/captable/companies/${companyId}/import`,
      formData
    );
  },

  // Utility Functions
  async recalculateCapTable(companyId: string): Promise<CapTableSnapshot> {
    const raw = await apiClient.post<CapTableSnapshot>(
      `/captable/companies/${companyId}/recalculate`
    );
    return mapCapTableSnapshot(raw);
  },

  async auditCapTable(companyId: string): Promise<CapTableValidationResult> {
    return apiClient.post<CapTableValidationResult>(
      `/captable/companies/${companyId}/audit`
    );
  },

  async getTransactionDocuments(
    companyId: string,
    transactionId: string
  ): Promise<TransactionDocument[]> {
    return apiClient.get<TransactionDocument[]>(
      `/captable/companies/${companyId}/transactions/${transactionId}/documents`
    );
  },

  async linkTransactionDocument(
    companyId: string,
    transactionId: string,
    data: AttachDocumentToTransactionRequest
  ): Promise<TransactionDocument> {
    return apiClient.post<TransactionDocument>(
      `/captable/companies/${companyId}/transactions/${transactionId}/documents`,
      data
    );
  },

  async unlinkTransactionDocument(
    companyId: string,
    transactionId: string,
    linkId: string
  ): Promise<void> {
    return apiClient.delete<void>(
      `/captable/companies/${companyId}/transactions/${transactionId}/documents/${linkId}`
    );
  },

  async getTransactionReconciliation(
    companyId: string,
    transactionId: string
  ): Promise<ReconciliationState> {
    return apiClient.get<ReconciliationState>(
      `/captable/companies/${companyId}/transactions/${transactionId}/reconciliation`
    );
  },

  async manualApproveTransaction(
    companyId: string,
    transactionId: string,
    data: ManualApproveRequest
  ): Promise<ReconciliationState> {
    return apiClient.post<ReconciliationState>(
      `/captable/companies/${companyId}/transactions/${transactionId}/manual-approve`,
      data
    );
  },

  async listTransactionBankEntries(
    companyId: string,
    transactionId: string
  ): Promise<BankEntry[]> {
    const entries = await apiClient.get<BankEntry[]>(
      `/captable/companies/${companyId}/transactions/${transactionId}/bank-entries`
    );
    return entries.map(mapBankEntry);
  },

  async listTransactionFundTransactions(
    companyId: string,
    transactionId: string
  ): Promise<CaptableFundTransaction[]> {
    const funds = await apiClient.get<CaptableFundTransaction[]>(
      `/captable/companies/${companyId}/transactions/${transactionId}/fund-transactions`
    );
    return funds.map(mapCaptableFundTransaction);
  },

  async addBankEntry(
    companyId: string,
    transactionId: string,
    data: AddBankEntryRequest
  ): Promise<BankEntry> {
    const created = await apiClient.post<BankEntry>(
      `/captable/companies/${companyId}/transactions/${transactionId}/bank-entries`,
      data
    );
    return mapBankEntry(created);
  },

  async updateBankEntry(
    companyId: string,
    transactionId: string,
    entryId: string,
    data: UpdateBankEntryRequest
  ): Promise<BankEntry> {
    const updated = await apiClient.put<BankEntry>(
      `/captable/companies/${companyId}/transactions/${transactionId}/bank-entries/${entryId}`,
      data
    );
    return mapBankEntry(updated);
  },

  async deleteBankEntry(
    companyId: string,
    transactionId: string,
    entryId: string
  ): Promise<void> {
    await apiClient.delete(
      `/captable/companies/${companyId}/transactions/${transactionId}/bank-entries/${entryId}`
    );
  },

  async adminApproveTransaction(
    companyId: string,
    transactionId: string
  ): Promise<ReconciliationState> {
    return apiClient.post<ReconciliationState>(
      `/captable/companies/${companyId}/transactions/${transactionId}/admin-approve`
    );
  },

  async deriveConversion(
    companyId: string,
    convertibleId: string,
    data: DeriveConversionRequest
  ): Promise<DeriveConversionResponse> {
    return apiClient.post<DeriveConversionResponse>(
      `/captable/companies/${companyId}/convertibles/${convertibleId}/derive-conversion`,
      data
    );
  },
};
